import { tt } from '@agos/shared'

/**
 * The notification delivery log — FRONTEND.md §9.253, FB-413.
 *
 * `GET /notifications/deliveries` records every attempt and nothing read it, so *did the customer get
 * their SMS* had no answer anywhere in the manager. **64 rows on this data**, every one of them
 * `no_recipient`, newest 2026-09-07.
 *
 * Field names read off the running endpoint, not inferred: `eventKey`, `channel`, `attempt`,
 * `outcome`, `providerResponse`, `latencyMs`, `orderId`, `customerId`, `createdAt`.
 */

export interface DeliveryRow {
  id: number
  eventKey: string
  channel: string
  /** Null on this data for every row — a push that had nobody to go to. */
  recipient: string | null
  attempt: number
  outcome: string
  providerResponse: string | null
  latencyMs: number
  orderId: number | null
  createdAt: string | null
}

export function deliveriesOf(payload: unknown): DeliveryRow[] {
  if (!Array.isArray(payload)) return []
  return payload.flatMap((entry) => {
    const row = entry as Record<string, unknown>
    if (typeof row.id !== 'number') return []
    return [
      {
        id: row.id,
        eventKey: typeof row.eventKey === 'string' ? row.eventKey : '',
        channel: typeof row.channel === 'string' ? row.channel : '',
        recipient: typeof row.recipient === 'string' && row.recipient !== '' ? row.recipient : null,
        attempt: typeof row.attempt === 'number' ? row.attempt : 1,
        outcome: typeof row.outcome === 'string' ? row.outcome : '',
        providerResponse:
          typeof row.providerResponse === 'string' && row.providerResponse !== ''
            ? row.providerResponse
            : null,
        latencyMs: typeof row.latencyMs === 'number' ? row.latencyMs : 0,
        orderId: typeof row.orderId === 'number' ? row.orderId : null,
        createdAt: typeof row.createdAt === 'string' ? row.createdAt : null,
      },
    ]
  })
}

/**
 * What an outcome means, in three groups rather than one enum key per row.
 *
 * **`no_recipient` is its own answer and neither of the other two.** It is the push channel firing for
 * a customer with no subscription — 114 subscriptions across 2 463 customers is what that looks like
 * in practice. Tinting it as a failure would make a working system look broken; tinting it as a
 * success would hide that nothing arrived.
 */
export type DeliveryTone = 'sent' | 'failed' | 'nobody' | 'pending'

export function outcomeTone(outcome: string): DeliveryTone {
  if (outcome === 'sent' || outcome === 'delivered') return 'sent'
  if (outcome === 'no_recipient' || outcome === 'skipped') return 'nobody'
  if (outcome === 'retrying' || outcome === 'queued' || outcome === '') return 'pending'
  return 'failed'
}

export function outcomeLabel(outcome: string): string {
  const tone = outcomeTone(outcome)
  if (tone === 'sent') return tt('Odoslané')
  if (tone === 'nobody') return tt('Nebolo komu')
  if (tone === 'pending') return tt('Čaká')
  return tt('Zlyhalo')
}

/** How the log reads at a glance — a count per group, over what came back. */
export interface DeliverySummary {
  total: number
  sent: number
  failed: number
  nobody: number
  pending: number
}

export function summariseDeliveries(rows: readonly DeliveryRow[]): DeliverySummary {
  const summary: DeliverySummary = { total: rows.length, sent: 0, failed: 0, nobody: 0, pending: 0 }
  for (const row of rows) summary[outcomeTone(row.outcome)] += 1
  return summary
}

/**
 * Said when the log is as long as the limit — `CLAUDE.md`'s no-silent-caps rule.
 *
 * A log showing the first hundred of a thousand and saying nothing reads as *a hundred notifications
 * were sent*, which is a different fact.
 */
export function capNote(rows: readonly DeliveryRow[], limit: number): string {
  return rows.length >= limit
    ? `${tt('Zobrazených je posledných')} ${limit} ${tt('pokusov - starších môže byť viac.')}`
    : ''
}
